import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { DocEntry } from '../types'

/** Derive the canonical URL for a doc entry. */
function docPath(doc: DocEntry): string {
  if (doc.slug === 'readme') return '/developers/onboarding'
  if (doc.isExample) return `/developers/onboarding/examples/${doc.slug}`
  return `/developers/onboarding/${doc.slug}`
}

interface DocPagerProps {
  prev: DocEntry | undefined
  next: DocEntry | undefined
  className?: string
}

/**
 * Prev/next pager rendered at the bottom of every doc page.
 * Skips the README from being a "next" target when on the last guide doc
 * since README is the index — navigating forward to it would feel backwards.
 */
export function DocPager({ prev, next, className }: DocPagerProps) {
  if (!prev && !next) return null

  return (
    <nav
      aria-label="Pagination"
      className={cn(
        'mt-12 flex items-stretch justify-between gap-4 border-t border-border pt-6',
        className,
      )}
    >
      {prev ? (
        <Link
          to={docPath(prev)}
          className={cn(
            'group flex min-w-0 flex-1 flex-col gap-1 rounded-md border border-border bg-bg px-4 py-3',
            'text-sm transition-colors hover:bg-muted',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
          )}
        >
          <span className="flex items-center gap-1 text-xs text-muted-fg">
            <ChevronLeft size={12} aria-hidden />
            Previous
          </span>
          <span className="truncate font-medium text-fg group-hover:text-[color:var(--brand-primary,hsl(var(--primary)))]">
            {prev.title}
          </span>
        </Link>
      ) : (
        <div className="flex-1" />
      )}

      {next ? (
        <Link
          to={docPath(next)}
          className={cn(
            'group flex min-w-0 flex-1 flex-col items-end gap-1 rounded-md border border-border bg-bg px-4 py-3',
            'text-sm transition-colors hover:bg-muted',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
          )}
        >
          <span className="flex items-center gap-1 text-xs text-muted-fg">
            Next
            <ChevronRight size={12} aria-hidden />
          </span>
          <span className="truncate font-medium text-fg group-hover:text-[color:var(--brand-primary,hsl(var(--primary)))]">
            {next.title}
          </span>
        </Link>
      ) : (
        <div className="flex-1" />
      )}
    </nav>
  )
}
